import React from "react";
import ReactDOM from "react-dom/client";
import "./index.scss";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import { store } from "./redux/store/store";
import { createTheme, ThemeProvider } from "@mui/material";

const root = ReactDOM.createRoot(document.getElementById("root"));
const theme = createTheme({
  typography: {
    fontFamily: "Maven Pro, sans-serif",
    allVariants: {
      color: "#FFFFFF",
    },
  },
  palette: {
    text: {
      primary: "#FFFFFF",
      secondary: "#686B78",
      dark: "#1E1E1E",
      gray: "#CCCCCC",
    },
    primary: {
      main: "#1A91AE",
    },
    secondary: {
      main: "#3783e7",
    },
    dark: {
      main: "#000000",
    },
    error: {
      main: "#cc5965",
    },
  },

  components: {
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: "#000", // Set text color
          "&:hover": {
            backgroundColor: "#e0e0e0", // Hover effect
          },
        },
        today: {
          backgroundColor: "#1976d2", // Blue for today's date
          color: "#fff", // White text for today
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#fff", // White background for modal
          color: "#000", // Black text
        },
      },
    },

    MuiClock: {
      styleOverrides: {
        root: {
          backgroundColor: "#fff", // White clock background
        },
        pin: {
          backgroundColor: "#1976d2", // Blue selection dot
        },
      },
    },
    MuiClockNumber: {
      styleOverrides: {
        root: {
          color: "#000", // Ensure clock numbers are visible
        },
        selected: {
          color: "#fff", // White text when a number is selected
          backgroundColor: "#1976d2", // Blue background for selected time
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "#ffffff", // White background for the dropdown
        },
        list: {
          backgroundColor: "#ffffff", // Ensures <ul> inside dropdown is white
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: "#1E1E1E !important", // Black text for list items
          "&:hover": {
            backgroundColor: "#f5f5f5", // Light gray background on hover
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          border: "none",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          // Style for the root element
          "& .MuiInputLabel-root": {
            color: "#F0F0F5", // Label color for all states
            "&.Mui-focused": {
              color: "#F0F0F5", // Focused label color
            },
          },
          "& .MuiInputBase-root": {
            color: "#F0F0F5", // Input text color for all states (fixes the visibility issue)
            backgroundColor: "#1E1E1E", // Input background color
          },
          "& .MuiOutlinedInput-root": {
            // Styles for the outlined variant
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#3D3D3D", // Default border color
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#3D3D3D", // Border color on hover
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#3D3D3D", // Border color when focused
            },
            "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
              borderColor: "#A0A0A5", // Border color for disabled state
            },
          },
        },
        // Optional: Add styles for the helper text
        helperText: {
          color: "#F0F0F5", // Helper text color
        },
      },
    },

    MuiButton: {
      variants: [
        {
          props: { variant: "custom-button" },
          style: {
            textTransform: "none",
            backgroundImage: "linear-gradient(-150deg, #1A91AE, #1AA49F)",
            color: "white",
            height: "44px",
            fontSize: 16,
            fontWeight: 600,
            borderRadius: "44px",
            "&.Mui-disabled": {
              color: "white",
              backgroundImage: "linear-gradient(-150deg, #808080, #808080)",
            },
          },
        },
        {
          props: { variant: "dark-button" },
          style: {
            textTransform: "none",
            backgroundColor: "#1E1E1E",
            color: "#FFF",
            height: "44px",
            fontSize: 16,
            fontWeight: 600,
            borderRadius: "44px",
            "&:hover": {
              textTransform: "none",
              backgroundColor: "#4E4E4E",
              color: "#FFF",
            },
            "&.Mui-disabled": {
              backgroundColor: "#8E8E8E",
              color: "#CCC",
              cursor: "not-allowed",
            },
          },
        },
        {
          props: { variant: "light-button" },
          style: {
            textTransform: "none",
            backgroundColor: "#FFFF",
            color: "#1E1E1E",
            height: "44px",
            fontSize: 16,
            fontWeight: 600,
            borderRadius: "44px",
            "&:hover": {
              textTransform: "none",
              backgroundColor: "#CCC",
              color: "#4E4E4E",
            },
            "&.Mui-disabled": {
              backgroundColor: "#AAA",
              color: "#8E8E8E",
              cursor: "not-allowed",
            },
          },
        },
        {
          props: { variant: "secondary-button" },
          style: {
            textTransform: "none",
            backgroundColor: "gray",
            color: "white",
            height: "44px",
            fontSize: 16,
            fontWeight: 600,
            borderRadius: "44px",
            opacity: 0.7,
            "&:hover": {
              textTransform: "none",
              backgroundColor: "gray",
              color: "white",
              opacity: 1,
            },
            "&.Mui-disabled": {
              backgroundColor: "lightgray",
              color: "#b0b0b0",
              opacity: 0.5,
              cursor: "not-allowed",
            },
          },
        },
        {
          props: { variant: "underlined-text" },
          style: {
            textTransform: "none",
            backgroundColor: "transparent",
            color: "#FFFFFF",
            padding: 0,
            textDecoration: "underline",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#DDDDDD",
              textDecoration: "underline",
            },
          },
        },
        {
          props: { variant: "add-btn" },
          style: {
            textTransform: "none",
            backgroundColor: "#808080",
            color: "white",
            padding: "8px 16px",
            border: "1px solid #1e1e1e",

            "&:hover": {
              backgroundColor: "#909090", // Slightly different on hover
            },

            "&.Mui-disabled": {
              backgroundColor: "#707070", // Disabled background color
              color: "#eee", // Disabled text color
              border: "1px solid #1e1e1e",
            },
          },
        },
        {
          props: { variant: "adjust-btn" },
          style: {
            textTransform: "none",
            backgroundColor: "#808080",
            color: "white",
            padding: "0px",
            border: "1px solid #1e1e1e",

            "&:hover": {
              backgroundColor: "#909090", // Background on hover
              // color: "#2D2F42",
              // borderColor: "#E0E0E5",
            },

            "&.Mui-disabled": {
              backgroundColor: "#D0D0D5",
              color: "#A0A0A5",
              borderColor: "#1e1e1e",
            },
          },
        },
      ],
    },
    MuiLoadingButton: {
      variants: [
        {
          props: { variant: "custom-button" },
          style: {
            textTransform: "none",
            backgroundImage: "linear-gradient(-150deg, #1ab394, #1a91ae)",
            color: "white",
          },
        },
      ],
    },
  },
});
root.render(
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <App />
    </ThemeProvider>
  </Provider>
);
